// src/screens/GameScreen.jsx
import { TimerBar } from "../components/ui.jsx";
import VotingPanel  from "../components/VotingPanel.jsx";
import PlayersTable from "../components/PlayersTable.jsx";
import ChatPanel    from "../components/ChatPanel.jsx";
import { PHASES, ROLES } from "../utils/constants.js";

// ── Phase meta ────────────────────────────────────────────────
const PHASE_META = {
  [PHASES.NIGHT]:  { label: "🌙 Night — Mafia Acts",   cls: "night",   bg: "rgba(122,20,20,0.28)",    color: "#e05555",      bdr: "rgba(122,20,20,0.5)" },
  [PHASES.SAVE]:   { label: "💊 Night — Doctor Saves",  cls: "save",    bg: "rgba(26,92,92,0.28)",     color: "#4db8b8",      bdr: "rgba(26,92,92,0.5)"  },
  [PHASES.DAY]:    { label: "☀️ Day — Town Votes",      cls: "day",     bg: "rgba(201,168,76,0.14)",   color: "var(--gold)",  bdr: "rgba(201,168,76,0.3)"},
  [PHASES.RESULT]: { label: "📜 Results",               cls: "result",  bg: "rgba(60,30,10,0.5)",      color: "#d4956a",      bdr: "rgba(180,100,30,0.4)"},
};
const ROLE_CHIP = {
  [ROLES.MAFIA]:   { bg: "rgba(122,20,20,0.4)",  color: "#e05555",      bdr: "rgba(122,20,20,0.6)"  },
  [ROLES.DOCTOR]:  { bg: "rgba(26,92,92,0.4)",   color: "#4db8b8",      bdr: "rgba(26,92,92,0.6)"   },
  [ROLES.CITIZEN]: { bg: "rgba(26,42,74,0.4)",   color: "#6080c0",      bdr: "rgba(26,42,74,0.6)"   },
};
const ROLE_LABEL = {
  [ROLES.MAFIA]:   "🔪 MAFIA",
  [ROLES.DOCTOR]:  "💊 DOCTOR",
  [ROLES.CITIZEN]: "🏛️ CITIZEN",
};
const PHASE_INSTR = (phase, role) => {
  const isMafia  = role === ROLES.MAFIA;
  const isDoctor = role === ROLES.DOCTOR;
  switch (phase) {
    case PHASES.NIGHT:
      return isMafia  ? "Choose your target below. Coordinate in the Mafia channel." : "The Mafia is choosing their target. Sleep tight...";
    case PHASES.SAVE:
      return isDoctor ? "Choose who to save tonight — even yourself." : "The Doctor is deliberating. Someone may be saved...";
    case PHASES.DAY:
      return "Vote to eliminate a suspect. Confirm to lock your vote — it cannot be changed.";
    case PHASES.RESULT:
      return "Waiting for the next round to begin...";
    default: return "";
  }
};

export default function GameScreen({
  state, me, localChat,
  onSendChat, onMafiaVote, onDoctorVote,
  onDayVote, onConfirmDayVote, onSkipVote, onQuitRoom,
}) {
  const phase  = state.phase;
  const pm     = PHASE_META[phase]   || { label: "⏳ Waiting", bg: "rgba(50,50,50,0.4)", color: "#aaa", bdr: "#444" };
  const rc     = ROLE_CHIP[me.role]  || ROLE_CHIP[ROLES.CITIZEN];
  const rl     = ROLE_LABEL[me.role] || "PLAYER";

  return (
    <div style={{ minHeight: "100vh", padding: "clamp(0.5rem, 2vw, 1rem)", display: "flex", flexDirection: "column", gap: "0.75rem", maxWidth: 1100, margin: "0 auto", width: "100%", animation: "fadeInUp 0.4s ease", boxSizing: "border-box" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 1rem", border: "1px solid rgba(201,168,76,0.18)", borderRadius: 3, background: "rgba(255,255,255,0.02)", flexWrap: "wrap", gap: "0.5rem" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(0.85rem, 2.5vw, 1.1rem)", color: "var(--gold)" }}>
          Round {state.round}
        </div>
        <div style={{ padding: "0.28rem 0.75rem", borderRadius: 2, fontSize: "clamp(0.55rem, 1.5vw, 0.65rem)", letterSpacing: "0.18em", fontWeight: 700, textTransform: "uppercase", background: pm.bg, color: pm.color, border: `1px solid ${pm.bdr}` }}>
          {pm.label}
        </div>
        <div style={{ padding: "0.28rem 0.75rem", borderRadius: 2, fontSize: "clamp(0.55rem, 1.5vw, 0.65rem)", letterSpacing: "0.14em", background: rc.bg, color: rc.color, border: `1px solid ${rc.bdr}` }}>
          {rl}
        </div>
        <button
          onClick={onQuitRoom}
          title={me.isHost ? "End room for everyone" : "Leave this room"}
          style={{
            padding: "0.28rem 0.6rem",
            borderRadius: 2,
            fontSize: "clamp(0.55rem, 1.5vw, 0.65rem)",
            letterSpacing: "0.12em",
            background: "rgba(139,26,26,0.15)",
            color: "#e05555",
            border: "1px solid rgba(139,26,26,0.4)",
            cursor: "pointer",
            fontFamily: "inherit",
            textTransform: "uppercase",
          }}
        >
          ✕ {me.isHost ? "End" : "Leave"}
        </button>
      </div>

      {/* ── Timer ── */}
      <TimerBar timerEndsAt={state.timerEndsAt} timerDuration={state.timerDuration} phase={phase} />

      {/* ── Body: main + chat ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr clamp(240px, 30vw, 340px)", gap: "0.75rem", flex: 1, minHeight: 0 }}>

        {/* LEFT: phase panel + voting + players */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: 0 }}>

          {/* Phase panel */}
          <div style={{ padding: "1rem", border: "1px solid rgba(201,168,76,0.14)", borderRadius: 3, background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(0.9rem, 2.5vw, 1rem)", color: "var(--gold)", marginBottom: "0.5rem" }}>
              {{ [PHASES.NIGHT]: "🌙 Mafia Phase", [PHASES.SAVE]: "💊 Doctor Phase", [PHASES.DAY]: "☀️ Town Vote", [PHASES.RESULT]: "📜 Round Result" }[phase] || "Phase"}
            </div>
            <div style={{ fontSize: "0.78rem", color: "rgba(242,234,216,0.55)", lineHeight: 1.65, marginBottom: "0.75rem" }}>
              {PHASE_INSTR(phase, me.role)}
            </div>

            {/* Voting UI */}
            <VotingPanel
              state={state} me={me}
              onMafiaVote={onMafiaVote}
              onDoctorVote={onDoctorVote}
              onDayVote={onDayVote}
              onConfirmDayVote={onConfirmDayVote}
              onSkipVote={onSkipVote}
            />
          </div>

          {/* Players table */}
          <PlayersTable state={state} me={me} />
        </div>

        {/* RIGHT: chat panel */}
        <ChatPanel
          state={state} me={me}
          localChat={localChat}
          onSendChat={onSendChat}
          style={{ height: "100%", maxHeight: "calc(100vh - 160px)", minHeight: 300 }}
        />
      </div>

      {/* ── Mobile: chat goes below on small screens ── */}
      <style>{`
        @media (max-width: 640px) {
          .game-body { grid-template-columns: 1fr !important; }
          .chat-panel-wrap { max-height: 220px !important; min-height: 180px !important; }
        }
      `}</style>
    </div>
  );
}
